import type { ApiResponse } from './ApiResponse.js'

/** The people an org admin has put under one leader, in one organization. */
export type LeaderScope = {
  id: string
  organizationId: string
  leaderUserId: string
  /** Explicit list. Picking a whole team or sprint in the UI only expands into this. */
  userIds: string[]
  createdAt: string
  updatedAt: string
  updatedByUserId: string
}

export enum LeaderInsightsStatus {
  READY = 'ready',
  /** Too few people with results for group numbers; only the group personality may be there. */
  NOT_ENOUGH_PEOPLE = 'not_enough_people',
  /** Nobody in scope has an analysed session or a personality test yet. */
  NO_DATA = 'no_data'
}

export enum LeaderMetricDirection {
  INCREASE = 'increase',
  DECREASE = 'decrease',
  BALANCE = 'balance'
}

/** Where a 0–1 result falls: low, in the middle, or high. Each surface names the three in its own words. */
export enum LeaderScoreZone {
  LOW = 'low',
  MIDDLE = 'middle',
  HIGH = 'high'
}

/** How high a behaviour's 0–1 measure is, in six bands the page names in sentences. */
export enum LeaderCollaborationLevel {
  VERY_LOW = 'very-low',
  LOW = 'low',
  BELOW_HALF = 'below-half',
  ABOVE_HALF = 'above-half',
  HIGH = 'high',
  VERY_HIGH = 'very-high'
}

export type LeaderScoreBucket = { fromInclusive: number; toExclusive: number; count: number }

/** The lowest and highest score and how many people fall in each range, for a group large enough that nobody stands out. */
export type LeaderScoreSpread = { min: number; max: number; buckets: LeaderScoreBucket[] }

/** One normalized metric of the group or of a team. `variable` is the datapoint key, e.g. `playerAskForHelp`. */
export type LeaderRankedMetric = {
  variable: string
  normalizedScore: number
  /** What the page says about it: for the group, how many of the people do it well; for a team, its own result. */
  level: LeaderCollaborationLevel
  recommendationDirection: LeaderMetricDirection
}

/** How one of Aria's points about the group reads: something that works, or something that needs attention. */
export enum LeaderHighlightTone {
  STRENGTH = 'strength',
  ATTENTION = 'attention'
}

/** One short point Aria makes about the group: a title of a few words and one sentence. */
export type LeaderHighlight = { tone: LeaderHighlightTone; title: string; detail: string }

/** Something short a leader could try for one behaviour of the group, e.g. `playerAskForHelp`. */
export type LeaderMetricSuggestion = { variable: string; suggestion: string }

/** A group average for one survey question or one personality trait. */
export type LeaderKeyedMean = { key: string; mean: number }

export type LeaderIndividualPsychologicalSafety = {
  /** How many people the numbers come from. */
  peopleCount: number
  /** The group average, 0–1. */
  mean: number
  spread?: LeaderScoreSpread
  /** Group average per survey question enough people answered, keys `playerReflectionQuestionOne..Seven`. */
  questions: LeaderKeyedMean[]
}

/** The results of one whole team. */
export type LeaderTeamInsight = {
  teamId: string
  teamName: string
  memberCount: number
  sessionCount: number
  teamHealth?: number
  /** Keys `teamReflectionQuestionOne..Six`, averaged over the team's sessions. */
  psychologicalSafetyQuestions: LeaderKeyedMean[]
  strengths: LeaderRankedMetric[]
  weaknesses: LeaderRankedMetric[]
}

export type LeaderInsights = {
  id: string
  organizationId: string
  leaderUserId: string
  status: LeaderInsightsStatus
  generatedAt: string
  /** Where a result turns middle and high, as these results were computed with. */
  scoreZoneFloors?: { middleFrom: number; highFrom: number }
  /** Where a survey question's whole score out of five turns middle and high. */
  questionZoneFloors?: { middleFrom: number; highFrom: number }
  peopleInScope: number
  /** People with at least one analysed session. */
  peopleWithSessions: number
  /** People with a completed personality test, whether or not they have played. */
  peopleWithPersonality: number
  sessionCount: number
  individualPsychologicalSafety?: LeaderIndividualPsychologicalSafety
  groupStrengths: LeaderRankedMetric[]
  groupWeaknesses: LeaderRankedMetric[]
  /** Group average per self-assessed personality trait, keys of `PersonalityScores`. */
  groupPersonality: LeaderKeyedMean[]
  teams: LeaderTeamInsight[]
  /** Aria's points about the group: what works, how safe people feel, what needs attention. */
  leadershipHighlights?: LeaderHighlight[]
  /** The one thing Aria suggests the leader tries next, written with her points. */
  leadershipSuggestion?: string
  /** Something to try for each of `groupWeaknesses`, written with her points. */
  groupWeaknessSuggestions?: LeaderMetricSuggestion[]
  /** Her note as one paragraph, for Aria to read back; the page shows it when there are no points. */
  leadershipSummary?: string
  /** The sessions the numbers were computed from. */
  sourceSessionIds: string[]
  /** The shape these results were built in. */
  formatVersion?: number
}

export type LeaderInsightsView = Omit<LeaderInsights, 'sourceSessionIds'>

export enum LeaderInsightsTaskStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed'
}

/** One regeneration job per leader per organization, in `leader_insights_tasks`. */
export type LeaderInsightsTask = {
  id: string
  organizationId: string
  leaderUserId: string
  status: LeaderInsightsTaskStatus
  attemptCount: number
  queuedAt: string
  startedAt?: string
  completedAt?: string
  /** Stamped on every write, so a task nobody has touched for a while can be told apart from a live one. */
  updatedAt?: string
  error?: string
  /** New data arrived while a run was in progress, so the finished run queues itself once more. */
  rerunRequested?: boolean
  /** When the list was last changed, so a run waits while it is still being edited. */
  listChangedAt?: string
}

// Callables

export type LeaderScopeMember = { userId: string; displayName?: string; email?: string; teamIds?: string[] }

export type GetLeaderScopeRequest = { organizationId?: string; leaderUserId: string }
export type GetLeaderScopeResponse = ApiResponse<{ scope?: LeaderScope; members: LeaderScopeMember[] }>

export type SetLeaderScopeRequest = { organizationId?: string; leaderUserId: string; userIds: string[] }
export type SetLeaderScopeResponse = ApiResponse<{ scope: LeaderScope }>

/** `leaderUserId` names whose results are asked for; without it, the caller's own. */
export type GetLeaderInsightsRequest = { organizationId?: string; leaderUserId?: string }

/** One person a leader leads, as the page lists them: a name and the teams they play in. */
export type LeaderPersonView = { displayName: string; teamNames: string[] }

export type LeaderInsightsResult = {
  /** False when leader features are off globally or for this organization. */
  featureEnabled: boolean
  insights?: LeaderInsightsView
  /** The people the leader leads, in alphabetical order. */
  people: LeaderPersonView[]
  /** A regeneration is queued or running; the client polls faster until it clears. */
  isRefreshing: boolean
}
export type GetLeaderInsightsResponse = ApiResponse<LeaderInsightsResult>
